import { AppCard } from "@lib/components/AppComponents";
import { Empty, Skeleton, Typography, Badge, Row, Col, Button } from "antd";
import { UseQueryProps } from "@lib/types";
import { XAICallLogsDashboardResponse } from "@lib/types/xAI";
import { useMemo } from "react";
import { FaRobot } from "react-icons/fa";
import styled from "styled-components";

type Props = {
  query?: UseQueryProps<XAICallLogsDashboardResponse>;
  extra?: string | JSX.Element;
  navigate?: () => void;
  loading?: boolean;
  refetch?: () => void;
};

const AgentCard = styled.div<{ status: string }>`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ status, theme }) => {
      switch (status) {
        case "KONUŞUYOR":
          return "linear-gradient(90deg, #52c41a 0%, #73d13d 100%)";
        case "BEKLİYOR":
          return "linear-gradient(90deg, #faad14 0%, #ffc53d 100%)";
        case "ÇEVRİMDIŞI":
          return "linear-gradient(90deg, #8c8c8c 0%, #bfbfbf 100%)";
        default:
          return theme.palette.primary.main;
      }
    }};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.12);

    &::before {
      opacity: 1;
    }
  }
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const AgentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const RobotIconWrapper = styled.div<{ status: string }>`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${({ theme, status }) => {
    switch (status) {
      case "KONUŞUYOR":
        return "linear-gradient(135deg, rgba(82, 196, 26, 0.15) 0%, rgba(82, 196, 26, 0.05) 100%)";
      case "BEKLİYOR":
        return "linear-gradient(135deg, rgba(250, 173, 20, 0.15) 0%, rgba(250, 173, 20, 0.05) 100%)";
      case "ÇEVRİMDIŞI":
        return "linear-gradient(135deg, rgba(140, 140, 140, 0.15) 0%, rgba(140, 140, 140, 0.05) 100%)";
      default:
        return theme.palette.background.secondary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  svg {
    font-size: 26px;
    color: ${({ theme, status }) => {
      switch (status) {
        case "KONUŞUYOR":
          return "#52c41a";
        case "BEKLİYOR":
          return "#faad14";
        case "ÇEVRİMDIŞI":
          return "#8c8c8c";
        default:
          return theme.palette.primary.main;
      }
    }};
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }
`;

const StatusBadge = styled(Badge)<{ status: string }>`
  .ant-badge-status-dot {
    width: 10px;
    height: 10px;
  }

  ${({ status }) => {
    switch (status) {
      case "KONUŞUYOR":
        return "color: #52c41a;";
      case "BEKLİYOR":
        return "color: #faad14;";
      case "ÇEVRİMDIŞI":
        return "color: #8c8c8c;";
      default:
        return "color: #8c8c8c;";
    }
  }}
`;

const MetricBox = styled.div`
  background: ${({ theme }) => theme.palette.background.secondary};
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  flex: 1;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    background: ${({ theme }) => theme.palette.background.paper};
    border-color: rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }
`;

const MetricLabel = styled(Typography.Text)`
  display: block;
  font-size: 11px;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-bottom: 6px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const MetricValue = styled(Typography.Text)`
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  letter-spacing: -0.5px;
`;

// Demo data generator
const generateDemoAgentData = () => {
  const companies = [
    "GLOBAL MARKETS FX",
    "PRIME TRADING CO.",
    "BLUE CHIP CAPITAL",
  ];

  const statuses = ["KONUŞUYOR", "BEKLİYOR", "ÇEVRİMDIŞI"];

  const agents = [
    { name: "Agent-Alpha", id: "1092" },
    { name: "Sales-Bot-01", id: "2092" },
    { name: "Support-Beta", id: "3092" },
    { name: "Crm-Genie", id: "4092" },
    { name: "Loan-Assistant", id: "5092" },
    { name: "Insurance-Compass", id: "6092" },
  ];

  return agents.map((agent, index) => {
    const status = statuses[index % statuses.length];
    const calls = Math.floor(Math.random() * 100) + 40;
    const positive = Math.floor(calls * 0.2);
    const cost = (Math.random() * 100 + 40).toFixed(2);

    return {
      ...agent,
      company: companies[index % companies.length],
      status,
      calls,
      positive,
      cost: `$${cost}`,
    };
  });
};

const TftSalesAgentPerformance = (props: Props) => {
  const { query, extra, navigate, loading } = props;
  const { data, isLoading, isFetching } = query || {};

  // Try to get agent data from API, fallback to demo
  const agentData = useMemo(() => {
    // Try to extract from groupByAIModelCount if available
    const groupByAIModel = data?.data?.groupByAIModelCount || [];

    if (groupByAIModel.length > 0) {
      // Use real data when available
      return groupByAIModel.map((item, index) => {
        const statuses = ["KONUŞUYOR", "BEKLİYOR", "ÇEVRİMDIŞI"];
        return {
          name: item.ai_model_name || `Agent-${index + 1}`,
          id: item.ai_model_id || `${index + 1}092`,
          company: "Unknown", // Not available in API
          status: statuses[index % statuses.length],
          calls: item.count,
          positive: Math.floor(item.count * 0.2), // Estimate
          cost: `$${(item.count * 0.8).toFixed(2)}`, // Estimate
        };
      });
    }

    // Fallback to demo data
    return generateDemoAgentData();
  }, [data]);

  return (
    <AppCard
      title="AI AGENT PERFORMANSI"
      extra={
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {extra}
          {navigate && (
            <Button type="link" onClick={navigate}>
              TÜMÜNÜ GÖRÜNTÜLE
            </Button>
          )}
        </div>
      }
      style={{ marginBottom: 16 }}
    >
      {isLoading || isFetching || loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          {agentData.length === 0 ? (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <Empty />
            </div>
          ) : (
            <Row gutter={[20, 20]}>
              {agentData.map((agent) => (
                <Col xs={24} sm={12} lg={8} key={agent.id}>
                  <AgentCard status={agent.status}>
                    <AgentHeader>
                      <AgentInfo>
                        <RobotIconWrapper status={agent.status}>
                          <FaRobot />
                        </RobotIconWrapper>
                        <div>
                          <Typography.Text
                            strong
                            style={{
                              display: "block",
                              fontSize: 15,
                              fontWeight: 700,
                              marginBottom: 4,
                            }}
                          >
                            {agent.name}
                          </Typography.Text>
                          <Typography.Text
                            type="secondary"
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              letterSpacing: "0.3px",
                            }}
                          >
                            ID: {agent.id}
                          </Typography.Text>
                        </div>
                      </AgentInfo>
                      <StatusBadge
                        status={agent.status}
                        text={
                          <Typography.Text
                            style={{
                              color:
                                agent.status === "KONUŞUYOR"
                                  ? "#52c41a"
                                  : agent.status === "BEKLİYOR"
                                    ? "#faad14"
                                    : "#8c8c8c",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {agent.status}
                          </Typography.Text>
                        }
                      />
                    </AgentHeader>

                    <div style={{ marginBottom: 20 }}>
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                        }}
                      >
                        {agent.company}
                      </Typography.Text>
                    </div>

                    <div style={{ display: "flex", gap: 10 }}>
                      <MetricBox>
                        <MetricLabel>ARAMALAR</MetricLabel>
                        <MetricValue>{agent.calls}</MetricValue>
                      </MetricBox>
                      <MetricBox>
                        <MetricLabel>POZITIF</MetricLabel>
                        <MetricValue>{agent.positive}</MetricValue>
                      </MetricBox>
                      <MetricBox>
                        <MetricLabel>MALIYET</MetricLabel>
                        <MetricValue>{agent.cost}</MetricValue>
                      </MetricBox>
                    </div>
                  </AgentCard>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </AppCard>
  );
};

export default TftSalesAgentPerformance;
